// HotelsPage — Two-pane: 300px filter sidebar + results grid.
// All CTAs → notYetWiredToast (hotels BE doesn't exist).
// TODO(hotels-API-PR): Replace HOTELS mock with /api/v1/hotels/search

import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SlidersHorizontal, Map, List, ChevronDown, X } from 'lucide-react';
import { HotelCard } from '@/components/HotelCard';
import { notYetWiredToast } from '@/components/primitives';
import { HOTELS } from '@/lib/hotels-mock';
import { useIsMobile } from '@/hooks/useIsMobile';

const T = {
  bg: '#FFF5EE', surface: '#FFFFFF', ink: '#1A0F08', inkDim: '#7A6657',
  primary: '#FF5A3C', soft: '#FFE3D2', chipInk: '#B5391C',
  line: 'rgba(26,15,8,0.08)', lineStr: '#E8E0D8',
  sans: '"Inter Variable","Inter",system-ui,sans-serif',
  display: '"Fraunces Variable","Fraunces",Georgia,serif',
};

type SortKey = 'recommended' | 'price-asc' | 'distance' | 'rating';
type ViewMode = 'list' | 'map';

const STAR_OPTS = [3, 4, 5] as const;
const AMENITY_OPTS = ['Pool', 'Spa', 'Breakfast', 'Wi-Fi', 'Gym', 'Beachfront'];

const SORT_LABELS: Record<SortKey, string> = {
  recommended:  'Recommended',
  'price-asc':  'Price: low to high',
  distance:     'Distance',
  rating:       'Rating',
};

interface ActiveChip {
  label: string;
  onRemove: () => void;
}

export function HotelsPage() {
  const navigate = useNavigate();
  const isMobile = useIsMobile();

  // filter state
  const [priceMin, setPriceMin] = useState(0);
  const [priceMax, setPriceMax] = useState(100000);
  const [stars, setStars] = useState<Set<number>>(new Set());
  const [freeCancelOnly, setFreeCancelOnly] = useState(false);
  const [amenities, setAmenities] = useState<Set<string>>(new Set());

  // ui state
  const [sort, setSort] = useState<SortKey>('recommended');
  const [view, setView] = useState<ViewMode>('list');
  const [sortOpen, setSortOpen] = useState(false);

  // derived
  const filtered = HOTELS
    .filter(h => {
      const priceInr = h.price * 83;
      if (priceInr < priceMin || priceInr > priceMax) return false;
      if (stars.size > 0 && !stars.has(h.stars)) return false;
      if (freeCancelOnly && !h.freeCancellation) return false;
      if (amenities.size > 0) {
        const hasAll = [...amenities].every(a =>
          h.perks.some(p => p.toLowerCase().includes(a.toLowerCase())),
        );
        if (!hasAll) return false;
      }
      return true;
    })
    .sort((a, b) => {
      if (sort === 'price-asc') return a.price - b.price;
      if (sort === 'rating') return b.rating - a.rating;
      return 0;
    });

  const activeChips: ActiveChip[] = [
    { label: 'Bengaluru ×', onRemove: () => {} },
    { label: '2 adults ×', onRemove: () => {} },
    { label: 'Mar 15–18 ×', onRemove: () => {} },
    ...([...stars].map(s => ({ label: `${s}★ ×`, onRemove: () => { const ns = new Set(stars); ns.delete(s); setStars(ns); } }))),
    ...(freeCancelOnly ? [{ label: 'Free cancel ×', onRemove: () => setFreeCancelOnly(false) }] : []),
    ...([...amenities].map(a => ({ label: `${a} ×`, onRemove: () => { const na = new Set(amenities); na.delete(a); setAmenities(na); } }))),
  ];

  function toggleStar(s: number) {
    setStars(prev => { const n = new Set(prev); if (n.has(s)) n.delete(s); else n.add(s); return n; });
  }
  function toggleAmenity(a: string) {
    setAmenities(prev => { const n = new Set(prev); if (n.has(a)) n.delete(a); else n.add(a); return n; });
  }

  return (
    <div style={{ background: T.bg, minHeight: '100vh' }}>
      <div style={{
        display: 'flex',
        flexDirection: isMobile ? 'column' : 'row',
        maxWidth: 1400,
        margin: '0 auto',
        padding: '0 clamp(16px, 4vw, 32px) 80px',
      }}>

        {/* ── Left sidebar 300px on desktop / full-width collapsible on mobile ── */}
        <aside style={{
          width: isMobile ? '100%' : 300,
          flexShrink: 0,
          paddingTop: 32,
          paddingRight: isMobile ? 0 : 32,
          position: isMobile ? 'static' : 'sticky',
          top: 80,
          alignSelf: 'flex-start',
          maxHeight: isMobile ? 'none' : 'calc(100vh - 100px)',
          overflowY: isMobile ? 'visible' : 'auto',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 24 }}>
            <SlidersHorizontal size={16} stroke={T.ink} strokeWidth={1.8}/>
            <span style={{ fontFamily: T.sans, fontSize: 13, fontWeight: 600, color: T.ink }}>Filters</span>
          </div>

          {/* Price range */}
          <FilterSection title="Price per night">
            <div style={{ display: 'flex', gap: 8, marginBottom: 10 }}>
              <PriceInput label="Min" value={priceMin} onChange={setPriceMin}/>
              <PriceInput label="Max" value={priceMax} onChange={setPriceMax}/>
            </div>
            <input
              type="range" min={0} max={100000} step={500}
              value={priceMax}
              onChange={e => setPriceMax(Number(e.target.value))}
              style={{ width: '100%', accentColor: T.primary }}
            />
            <div style={{ display: 'flex', justifyContent: 'space-between', marginTop: 4 }}>
              <span style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim }}>₹0</span>
              <span style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim }}>₹1,00,000</span>
            </div>
          </FilterSection>

          {/* Star rating */}
          <FilterSection title="Star rating">
            <div style={{ display: 'flex', gap: 6 }}>
              {STAR_OPTS.map(s => (
                <Chip
                  key={s}
                  active={stars.has(s)}
                  onClick={() => toggleStar(s)}
                  label={`${s}+`}
                />
              ))}
            </div>
          </FilterSection>

          {/* Free cancellation */}
          <FilterSection title="Cancellation">
            <label style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={freeCancelOnly}
                onChange={e => setFreeCancelOnly(e.target.checked)}
                style={{ width: 16, height: 16, accentColor: T.primary, cursor: 'pointer' }}
              />
              <span style={{ fontFamily: T.sans, fontSize: 13, color: T.ink }}>Free cancellation only</span>
            </label>
          </FilterSection>

          {/* Amenities */}
          <FilterSection title="Amenities">
            <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
              {AMENITY_OPTS.map(a => (
                <label key={a} style={{ display: 'flex', alignItems: 'center', gap: 8, cursor: 'pointer' }}>
                  <input
                    type="checkbox"
                    checked={amenities.has(a)}
                    onChange={() => toggleAmenity(a)}
                    style={{ width: 15, height: 15, accentColor: T.primary, cursor: 'pointer' }}
                  />
                  <span style={{ fontFamily: T.sans, fontSize: 13, color: T.ink }}>{a}</span>
                </label>
              ))}
            </div>
          </FilterSection>
        </aside>

        {/* ── Main content ── */}
        <main style={{ flex: 1, paddingTop: 32 }}>
          {/* toolbar */}
          <div style={{ marginBottom: 20 }}>
            {/* query chips + sort + view toggle */}
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: 12, marginBottom: 14 }}>
              {/* active chips */}
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6 }}>
                {activeChips.map((chip, i) => (
                  <button
                    key={i}
                    onClick={chip.onRemove}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 4,
                      padding: '5px 10px', borderRadius: 999,
                      background: T.soft, color: T.chipInk,
                      fontFamily: T.sans, fontSize: 11, fontWeight: 500,
                      border: 'none', cursor: 'pointer',
                    }}
                  >
                    {chip.label}
                    <X size={10} stroke={T.chipInk} strokeWidth={2}/>
                  </button>
                ))}
              </div>

              <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
                {/* sort */}
                <div style={{ position: 'relative' }}>
                  <button
                    onClick={() => setSortOpen(o => !o)}
                    style={{
                      display: 'flex', alignItems: 'center', gap: 6,
                      padding: '8px 14px', borderRadius: 999,
                      background: T.surface, border: `1px solid ${T.lineStr}`,
                      fontFamily: T.sans, fontSize: 12, fontWeight: 500, color: T.ink,
                      cursor: 'pointer',
                    }}
                  >
                    {SORT_LABELS[sort]}
                    <ChevronDown size={13} stroke={T.inkDim} strokeWidth={1.8}/>
                  </button>
                  {sortOpen && (
                    <div style={{
                      position: 'absolute', top: '110%', right: 0, zIndex: 50,
                      background: T.surface, border: `1px solid ${T.lineStr}`,
                      borderRadius: 18, overflow: 'hidden',
                      boxShadow: '0 12px 24px -8px rgba(0,0,0,0.12)', minWidth: 200,
                    }}>
                      {(Object.entries(SORT_LABELS) as [SortKey, string][]).map(([k, label]) => (
                        <button
                          key={k}
                          onClick={() => { setSort(k); setSortOpen(false); }}
                          style={{
                            display: 'block', width: '100%', padding: '11px 16px',
                            background: sort === k ? T.soft : 'transparent',
                            border: 'none', cursor: 'pointer', textAlign: 'left',
                            fontFamily: T.sans, fontSize: 13,
                            color: sort === k ? T.chipInk : T.ink, fontWeight: sort === k ? 600 : 400,
                          }}
                        >{label}</button>
                      ))}
                    </div>
                  )}
                </div>

                {/* view toggle */}
                <div style={{
                  display: 'flex', background: T.surface, borderRadius: 999, padding: 3,
                  border: `1px solid ${T.lineStr}`,
                }}>
                  {(['list', 'map'] as ViewMode[]).map(v => (
                    <button
                      key={v}
                      onClick={() => v === 'map' ? notYetWiredToast('Map view') : setView(v)}
                      style={{
                        display: 'flex', alignItems: 'center', gap: 4,
                        padding: '6px 12px', borderRadius: 999, border: 'none',
                        background: view === v ? T.ink : 'transparent',
                        color: view === v ? '#FFF5EE' : T.inkDim,
                        fontFamily: T.sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
                        transition: 'background 150ms ease',
                      }}
                    >
                      {v === 'list' ? <List size={13} strokeWidth={2}/> : <Map size={13} strokeWidth={2}/>}
                      {v.charAt(0).toUpperCase() + v.slice(1)}
                    </button>
                  ))}
                </div>
              </div>
            </div>

            <div style={{
              fontFamily: T.display, fontSize: 36, fontWeight: 500, color: T.ink,
              lineHeight: 1, letterSpacing: -0.02,
            }}>
              {filtered.length} places to crash.
            </div>
            <div style={{ fontFamily: T.sans, fontSize: 13, color: T.inkDim, marginTop: 4 }}>
              Bengaluru · Mar 15→18 · 2 guests
            </div>
          </div>

          {/* hotel grid */}
          {filtered.length === 0 ? (
            <div style={{ padding: '48px 0', textAlign: 'center', color: T.inkDim, fontFamily: T.sans }}>
              No hotels match those filters. Try widening your search.
            </div>
          ) : (
            <div style={{
              display: 'grid',
              gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))',
              gap: 24,
            }}>
              {filtered.map(h => (
                <HotelCard
                  key={h.id}
                  hotel={h}
                  onClick={() => navigate(`/hotels/${h.id}`)}
                />
              ))}
            </div>
          )}
        </main>
      </div>
    </div>
  );
}

// ── Sub-components ──────────────────────────────────────────────────────────

function FilterSection({ title, children }: { title: string; children: React.ReactNode }) {
  return (
    <div style={{ marginBottom: 24 }}>
      <div style={{
        fontFamily: T.sans, fontSize: 11, color: T.inkDim,
        fontWeight: 600, letterSpacing: 1.5, textTransform: 'uppercase',
        marginBottom: 10,
      }}>{title}</div>
      {children}
    </div>
  );
}

function Chip({ label, active, onClick }: { label: string; active: boolean; onClick: () => void }) {
  return (
    <button
      onClick={onClick}
      style={{
        padding: '7px 14px', borderRadius: 999, border: 'none',
        background: active ? T.ink : T.surface,
        color: active ? '#FFF5EE' : T.ink,
        fontFamily: T.sans, fontSize: 12, fontWeight: 600, cursor: 'pointer',
        boxShadow: active ? 'none' : `0 0 0 1px ${T.lineStr}`,
        transition: 'background 150ms ease',
      }}
    >{label}</button>
  );
}

function PriceInput({ label, value, onChange }: { label: string; value: number; onChange: (v: number) => void }) {
  return (
    <div style={{ flex: 1 }}>
      <div style={{ fontFamily: T.sans, fontSize: 10, color: T.inkDim, marginBottom: 4 }}>{label}</div>
      <input
        type="number"
        value={value}
        onChange={e => onChange(Number(e.target.value))}
        style={{
          width: '100%', height: 36, borderRadius: 10, padding: '0 10px',
          border: `1px solid ${T.lineStr}`, background: T.surface,
          fontFamily: T.sans, fontSize: 12, color: T.ink, outline: 'none',
        }}
      />
    </div>
  );
}
